import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { hasPermission } from "../entities/access";
import type { Counterparty } from "../entities/counterparties";
import { shellBootstrapQuery } from "./bootstrap";
import { CounterpartyCard } from "./CounterpartyCard";
import {
  CounterpartyForm,
  type CounterpartyFormValues,
} from "./CounterpartyForm";
import {
  compactCounterpartiesSearch,
  counterpartiesTabs,
  newCounterpartyId,
  requestErrorText,
  useSubmissionKey,
  validateCounterpartiesSearch,
  type CounterpartiesSearch,
  type CounterpartiesTab,
} from "./counterpartiesSupport";
import { Pagination } from "./Pagination";
import { SenderResolutionQueue } from "./SenderResolutionQueue";
import { SenderRulesPanel } from "./SenderRulesPanel";
import type { ShellServices } from "./services";
import { ErrorPage } from "./states";
import { TabList } from "./TabList";
import { counterpartyKeys } from "./useCounterpartyNames";
import { Notice } from "./Notice";

const PAGE_SIZE = 10;

const tabLabels: Record<CounterpartiesTab, string> = {
  counterparties: "Контрагенты",
  rules: "Правила определения",
  inbox: "Письма без контрагента",
};

function CreateCounterparty({
  service,
  onCreated,
  onCancel,
}: {
  service: NonNullable<ShellServices["counterparties"]>;
  onCreated: (counterparty: Counterparty) => void;
  onCancel: () => void;
}) {
  const client = useQueryClient();
  const key = useSubmissionKey();
  const create = useMutation({
    mutationFn: async (values: CounterpartyFormValues) => {
      // Пустые реквизиты не отправляются: при создании `null` и отсутствие поля
      // для сервера одно и то же, а тело остаётся короче.
      const created = await service.create(
        {
          name: values.name,
          ...(values.inn === null ? {} : { inn: values.inn }),
          ...(values.kpp === null ? {} : { kpp: values.kpp }),
          ...(values.notes === null ? {} : { notes: values.notes }),
          ...(values.domains.length === 0 ? {} : { domains: values.domains }),
          ...(values.contacts.length === 0
            ? {}
            : { contacts: values.contacts }),
        },
        key.current(),
      );
      return created;
    },
    onSuccess: async (created) => {
      key.reset();
      client.setQueryData(counterpartyKeys.detail(created.id), created);
      await client.invalidateQueries({ queryKey: counterpartyKeys.all });
      onCreated(created);
    },
  });

  return (
    <div className="cp-card">
      <CounterpartyForm
        pending={create.isPending}
        error={create.error}
        onChange={() => key.reset()}
        onCancel={onCancel}
        onSubmit={(values) => create.mutate(values)}
      />
    </div>
  );
}

function CounterpartyDirectory({
  service,
  canManage,
  canCreate,
  allOrganizations,
  scopeName,
  search,
  onSearch,
}: {
  service: NonNullable<ShellServices["counterparties"]>;
  canManage: boolean;
  canCreate: boolean;
  allOrganizations: boolean;
  scopeName?: string;
  search: CounterpartiesSearch;
  onSearch: (patch: Partial<CounterpartiesSearch>) => void;
}) {
  const [text, setText] = useState(search.q);
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;
  // Запрос уходит не на каждую букву, а когда человек перестал печатать.
  // Новый поиск начинается с первой страницы: старый номер мог не существовать
  // в сузившемся результате.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (text.trim() !== search.q)
        onSearchRef.current({ q: text.trim(), page: 1 });
    }, 300);
    return () => clearTimeout(timer);
  }, [text, search.q]);
  // Переход «назад» меняет запрос в адресной строке: поле должно за ним следовать.
  useEffect(() => {
    setText((current) => (current.trim() === search.q ? current : search.q));
  }, [search.q]);

  const list = useQuery({
    queryKey: [...counterpartyKeys.all, "list", search.q, search.page],
    queryFn: () =>
      service.list({
        page: search.page,
        per_page: PAGE_SIZE,
        ...(search.q === "" ? {} : { search: search.q }),
      }),
    // Прошлая страница остаётся на экране, пока грузится следующая: без этого
    // список и пагинация пропадали бы на каждый клик по номеру страницы.
    placeholderData: keepPreviousData,
  });
  const items = list.data?.data ?? [];
  const meta = list.data?.meta;
  const total = meta?.total ?? 0;
  // Список короче, чем открытая страница (например, контрагента удалили или
  // поиск сузил выдачу) — возвращаемся на последнюю существующую страницу.
  useEffect(() => {
    if (meta !== undefined && meta.last_page > 0 && search.page > meta.last_page) {
      onSearchRef.current({ page: meta.last_page });
    }
  }, [meta, search.page]);
  const creating = search.id === newCounterpartyId;
  const opened = creating ? undefined : search.id;

  return (
    <div
      className="cp-layout"
      data-detail={search.id === undefined ? "closed" : "open"}
    >
      <div className="cp-list-pane">
        <div className="field cp-field cp-search">
          <label htmlFor="cp-search">
            <span>Поиск по названию или ИНН</span>
          </label>
          <input
            id="cp-search"
            type="search"
            value={text}
            maxLength={255}
            autoComplete="off"
            onChange={(event) => setText(event.target.value)}
          />
        </div>

        {list.isLoading ? (
          <p aria-busy="true">Загружаем контрагентов…</p>
        ) : null}
        {list.isError ? (
          <div className="cp-empty">
            <Notice>
              {requestErrorText(
                list.error,
                "Не удалось загрузить контрагентов.",
              )}
            </Notice>
            <button
              type="button"
              className="button button--secondary button--compact"
              onClick={() => void list.refetch()}
            >
              Повторить
            </button>
          </div>
        ) : null}
        {list.isSuccess && items.length === 0 ? (
          <div className="cp-empty">
            {search.q === "" ? (
              <p>
                {allOrganizations
                  ? "Ни в одной из доступных вам организаций контрагентов пока нет."
                  : scopeName === undefined
                    ? "В этой организации контрагентов пока нет."
                    : `В организации «${scopeName}» контрагентов пока нет.`}{" "}
                Справочник ведётся отдельно в каждой организации.{" "}
                {!canManage
                  ? "Добавлять контрагентов может сотрудник с правом «Управление контрагентами»."
                  : allOrganizations
                    ? "Чтобы добавить первого, выберите организацию в поле «Рабочая организация» в верхней панели."
                    : "Добавьте первого: по его контактам и доменам система будет определять отправителей писем."}
              </p>
            ) : (
              <p>По запросу «{search.q}» ничего не найдено.</p>
            )}
            {search.q === "" && canCreate ? (
              <button
                type="button"
                className="button button--secondary button--compact"
                onClick={() => onSearch({ id: newCounterpartyId })}
              >
                Добавить контрагента
              </button>
            ) : null}
          </div>
        ) : null}

        {items.length > 0 ? (
          <>
            <ul className="cp-list" aria-label="Контрагенты">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className="cp-list__item"
                    aria-current={item.id === opened ? "true" : undefined}
                    onClick={() => onSearch({ id: item.id })}
                  >
                    <span className="cp-list__title">{item.name}</span>
                    <span className="muted">
                      {[
                        item.inn ? `ИНН ${item.inn}` : null,
                        item.kpp ? `КПП ${item.kpp}` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "Реквизиты не указаны"}
                    </span>
                    <span className="cp-list__meta muted">
                      {item.contacts.length} конт. · {item.domains.length} дом.
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <Pagination
              page={meta?.current_page ?? search.page}
              lastPage={meta?.last_page ?? 1}
              total={total}
              perPage={PAGE_SIZE}
              label="Страницы списка контрагентов"
              busy={list.isFetching}
              onChange={(page) => onSearch({ page })}
            />
          </>
        ) : null}
      </div>

      <div className="cp-detail">
        {creating && canCreate ? (
          <CreateCounterparty
            service={service}
            onCancel={() => onSearch({ id: undefined })}
            onCreated={(created) => onSearch({ id: created.id })}
          />
        ) : opened !== undefined ? (
          <CounterpartyCard
            key={opened}
            service={service}
            id={opened}
            canManage={canManage}
            onBack={() => onSearch({ id: undefined })}
          />
        ) : (
          <div className="cp-empty cp-empty--detail">
            <p>
              Выберите контрагента в списке, чтобы увидеть реквизиты, контакты,
              домены и правила определения отправителя.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export function CounterpartiesPage({ services }: { services: ShellServices }) {
  const navigate = useNavigate();
  const locationSearch = useLocation({
    select: (location) => location.search as Record<string, unknown>,
  });
  const search = validateCounterpartiesSearch(locationSearch);
  const bootstrap = useQuery(shellBootstrapQuery(services));
  const service = services.counterparties;
  const canManage =
    bootstrap.data !== undefined &&
    hasPermission(
      { permissions: bootstrap.data.permissions },
      "document-workflow.counterparties.manage",
    );

  // В режиме «Все организации» видны данные всех доступных организаций и можно
  // менять существующее (Core проверяет право в организации записи), но новый
  // контрагент создаётся в одной организации, и выбрать её здесь не из чего.
  const allOrganizations = bootstrap.data?.allOrganizations === true;
  const canCreate = canManage && !allOrganizations;

  // Адресная строка — единственный источник состояния; поле со значением
  // undefined в патче убирается из неё.
  function setSearch(patch: Partial<CounterpartiesSearch>) {
    const next = validateCounterpartiesSearch({ ...search, ...patch });
    void navigate({
      to: "/modules/document-workflow/counterparties",
      search: compactCounterpartiesSearch(next),
      resetScroll: false,
    });
  }

  if (service === undefined) return <ErrorPage status={503} />;

  function selectTab(tab: CounterpartiesTab) {
    setSearch({ tab, id: undefined, message: undefined, q: "", page: 1 });
  }

  return (
    <section className="workspace-page cp-page" aria-labelledby="cp-title">
      <div className="dw-heading">
        <div>
          <h1 id="cp-title">Контрагенты</h1>
          <p>
            Справочник организаций, чьи письма приходят в документооборот, и
            правила, по которым система определяет отправителя.
          </p>
        </div>
        {canCreate && search.tab === "counterparties" ? (
          <button
            type="button"
            className="button button--primary"
            onClick={() => setSearch({ id: newCounterpartyId })}
          >
            Добавить контрагента
          </button>
        ) : null}
      </div>

      <TabList
        tabs={counterpartiesTabs}
        labels={tabLabels}
        active={search.tab}
        onSelect={selectTab}
        label="Разделы справочника"
        idPrefix="cp-tab"
        panelId="cp-panel"
      />

      <div
        id="cp-panel"
        role="tabpanel"
        aria-labelledby={`cp-tab-${search.tab}`}
        className="cp-panel"
      >
        {search.tab === "counterparties" ? (
          <CounterpartyDirectory
            service={service}
            canManage={canManage}
            canCreate={canCreate}
            allOrganizations={allOrganizations}
            scopeName={bootstrap.data?.currentScope?.name}
            search={search}
            onSearch={setSearch}
          />
        ) : null}
        {search.tab === "rules" ? (
          <SenderRulesPanel service={service} canManage={canManage} />
        ) : null}
        {search.tab === "inbox" ? (
          <SenderResolutionQueue
            service={service}
            canManage={canManage}
            selectedId={search.message}
            onSelect={(id) => setSearch({ message: id })}
          />
        ) : null}
      </div>
    </section>
  );
}
